import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@heroui/react";
import { K1DataTable } from "../components/K1DataTable";
import K1FilterBar from "../components/K1FilterBar";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import {
  buildFundScopeParams,
  buildK1DownloadUrl,
  k1Api,
  type K1FundSelection,
} from "../services/api";
import { MOST_RECENT } from "../constants";
import { getQuarterFromYYYYMM } from "../utils/quarters";
import { DownloadButton } from "@/components/DownloadButton";

function buildPositionsFilteredUrl(
  selection: K1FundSelection,
  period: string | null,
): string {
  const params = buildFundScopeParams(selection);
  if (period && period !== MOST_RECENT) {
    params.append("reporting_date_equals", period);
  } else {
    params.set("most_recent", "true");
  }
  return buildK1DownloadUrl("k1_positions", params);
}

type PositionRow = Record<string, unknown>;

const MAX_STICKY_FUND_CHARS = 58;

function truncateStickyFund(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (text.length <= MAX_STICKY_FUND_CHARS) return text;
  return `${text.slice(0, MAX_STICKY_FUND_CHARS)}...`;
}

function matchesSelectedPeriod(
  row: PositionRow,
  selectedPeriod: string | null,
): boolean {
  if (!selectedPeriod) return true;
  return getQuarterFromYYYYMM(row.reporting_date) === selectedPeriod;
}

const text = (
  key: string,
  header: string,
  size = 130,
): ColumnDef<PositionRow> => ({
  accessorKey: key,
  header,
  size,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    if (key === "fund_name") {
      const text = String(v);
      return <span title={text}>{truncateStickyFund(text)}</span>;
    }
    return String(v);
  },
});

const num = (
  key: string,
  header: string,
  size = 120,
): ColumnDef<PositionRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { className: "text-right" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      n,
    );
  },
});

const pct = (
  key: string,
  header: string,
  size = 90,
): ColumnDef<PositionRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { className: "text-right" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return (n * 100).toFixed(2) + "%";
  },
});

const mult = (
  key: string,
  header: string,
  size = 80,
): ColumnDef<PositionRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { className: "text-right" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return n.toFixed(2) + "x";
  },
});

const bool = (
  key: string,
  header: string,
  size = 80,
): ColumnDef<PositionRow> => ({
  accessorKey: key,
  header,
  size,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return v ? "Yes" : "No";
  },
});

const columns: ColumnDef<PositionRow>[] = [
  text("company_name_long", "Company Name", 200),
  text("fund_name", "Fund", 160),
  text("reporting_date", "Reporting Date", 110),
  text("reporting_currency_fund", "Reporting Currency", 100),
  text("asset_type", "Type of Investment", 140),
  text("investment_currency", "Inv. Currency", 90),
  num("total_investment_cost", "Total Investment Cost", 140),
  num("current_costs", "Active Invested Costs", 140),
  num("proceeds", "Proceeds", 110),
  num("realized_cost", "Realized Cost", 110),
  num("dividends_income", "Dividends / Income", 130),
  num("fair_value", "Fair Value (unrealized)", 140),
  pct("stake_non_diluted", "Stake % (non diluted)", 120),
  pct("stake_fully_diluted", "Stake % (fully diluted)", 130),
  pct("irr", "IRR", 80),
  mult("moic", "MOIC", 70),
  num("turnover", "Turnover", 100),
  text("company_investment_state", "Investment State", 160),
  text("first_investment_date", "First Investment Date", 130),
  text("date_of_partial_exit", "Date of Partial Exit", 130),
  text("exit_date", "Exit Date", 100),
  text("exit_method", "Exit Method", 120),
  bool("is_listed", "Public Equity", 80),
  pct("voting_rights", "Voting Rights", 100),
];

export default function PositionsView() {
  const {
    selectedFundIds,
    selectedPeriod,
    periodsPerFund,
    funds,
    fundSelection,
  } = useK1DashboardContext();

  const isMostRecent = selectedPeriod === MOST_RECENT;

  // When MOST_RECENT, fetch all (no period filter) so we can pick the most
  // recent period per fund client-side. Otherwise pass the specific period.
  const apiPeriod = isMostRecent ? null : selectedPeriod;

  const { data, isLoading } = useQuery({
    queryKey: ["k1", "positions-all", apiPeriod],
    queryFn: () =>
      k1Api.getPositions({ period: apiPeriod, selection: fundSelection }),
    staleTime: 60_000,
  });

  const issuerIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of (data?.data ?? []) as Record<string, unknown>[]) {
      const id = Number(row.issuer_id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [data]);

  const { data: issuersData } = useQuery({
    queryKey: ["k1", "issuers-by-ids", issuerIds],
    queryFn: () => k1Api.getIssuers({ ids: issuerIds }),
    staleTime: 60_000,
    enabled: issuerIds.length > 0,
  });

  // Build issuer_id → company_name_long lookup
  const issuerNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const iss of issuersData?.data ?? []) {
      const id = Number(iss.id ?? iss.issuer_id);
      if (id && iss.company_name_long)
        map.set(id, String(iss.company_name_long));
    }
    return map;
  }, [issuersData]);

  // Build fund_id → fund_name lookup
  const fundNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const f of funds) {
      const id = Number(f.id ?? f.fund_id);
      if (id && f.fund_name) map.set(id, f.fund_name);
    }
    return map;
  }, [funds]);

  const allRows = (data?.data ?? []) as PositionRow[];
  const rows = useMemo(() => {
    if (selectedFundIds.length === 0) return [];

    const fundRows = allRows.filter((r) =>
      selectedFundIds.includes(Number(r.fund_id)),
    );

    const filtered = isMostRecent
      ? fundRows.filter((r) => {
          // Keep only the row if it belongs to the most recent period for its fund
          const fundId = Number(r.fund_id);
          const mostRecentForFund = periodsPerFund.get(fundId)?.[0] ?? null;
          return matchesSelectedPeriod(r, mostRecentForFund);
        })
      : fundRows.filter((r) => matchesSelectedPeriod(r, selectedPeriod));

    return filtered.map((r) => ({
      ...r,
      company_name_long: issuerNameMap.get(Number(r.issuer_id)) ?? "",
      fund_name: fundNameMap.get(Number(r.fund_id)) ?? "",
    }));
  }, [
    allRows,
    selectedFundIds,
    selectedPeriod,
    isMostRecent,
    periodsPerFund,
    issuerNameMap,
    fundNameMap,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <K1FilterBar />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">Positions</h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {rows.length.toLocaleString()} positions
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <K1DataTable<PositionRow, unknown>
              table_name="k1-positions"
              columns={columns}
              data={rows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["company_name_long", "fund_name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildPositionsFilteredUrl(fundSelection, selectedPeriod)}
                filename={`k1_positions_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
